/**
 * Banka hareketinin yönü (bakiyeye etkisi) — Banka İşlemleri raporunda masaüstü/mobil GENEL TOPLAM,
 * satır rengi ve Excel aynı kuralı kullanır (backend MoneyTransactionDeleteRules.ResolveEffectSign ile aynı sıra):
 *   1) Type: INCOME → +1, EXPENSE → -1 (büyük/küçük harf duyarsız)
 *   2) Type boşsa açıklama ön eki: [GİRİŞ] → +1, [ÇIKIŞ] → -1 (eski banka↔kasa transfer bacakları)
 *   3) Eski transactionType listesi: withdrawal/withdraw/odeme/payment/expense → -1, deposit/income → +1
 *   4) Hâlâ belirsizse: negatif tutar → -1, değilse +1
 */

export const BANK_TX_IN_PREFIX = '[GİRİŞ]';
export const BANK_TX_OUT_PREFIX = '[ÇIKIŞ]';

export interface BankTxDirectionInput {
  type?: string | null;
  description?: string | null;
  transactionType?: string | null;
  amount?: number | null;
  currency?: string | null;
}

const OUT_TRANSACTION_TYPES = new Set(['WITHDRAWAL', 'WITHDRAW', 'ODEME', 'PAYMENT', 'EXPENSE']);
const IN_TRANSACTION_TYPES = new Set(['DEPOSIT', 'INCOME']);

// Türkçe yerel ayardan bağımsız büyük harf ("income" → "INCOME"; tr-TR'de "İNCOME" olurdu)
const upper = (v?: string | null): string => (v ?? '').trim().toLocaleUpperCase('en-US');

export const getBankTxDirection = (tx: BankTxDirectionInput): 1 | -1 => {
  const type = upper(tx.type);
  if (type === 'INCOME') return 1;
  if (type === 'EXPENSE') return -1;

  const desc = (tx.description ?? '').trimStart();
  if (desc.startsWith(BANK_TX_IN_PREFIX)) return 1;
  if (desc.startsWith(BANK_TX_OUT_PREFIX)) return -1;

  const tt = upper(tx.transactionType);
  if (OUT_TRANSACTION_TYPES.has(tt)) return -1;
  if (IN_TRANSACTION_TYPES.has(tt)) return 1;

  return (tx.amount ?? 0) < 0 ? -1 : 1;
};

/** Yönlü tutar: giriş +, çıkış − (tutarın kendi işareti yok sayılır). */
export const getBankTxSignedAmount = (tx: BankTxDirectionInput): number =>
  getBankTxDirection(tx) * Math.abs(tx.amount ?? 0);

/** Verilen para birimindeki hareketlerin yönlü toplamı (varsayılan TRY). */
export const sumBankTxSigned = (txs: BankTxDirectionInput[], currency: string = 'TRY'): number =>
  txs.reduce((total, tx) => ((tx.currency || 'TRY') === currency ? total + getBankTxSignedAmount(tx) : total), 0);

/** Transfer/virman bacağı mı? (silmede iki bacak birlikte silinir) */
export const isBankTransferLeg = (tx: { transactionType?: string | null; transferGroupId?: string | null }): boolean => {
  if (tx.transferGroupId) return true;
  const tt = upper(tx.transactionType);
  return tt === 'TRANSFER' || tt === 'VIRMAN';
};

export type BankTxLinkedSource = 'check' | 'personnel' | 'creditCard' | 'card';

/**
 * Fatura dışı bağlantılı hareket (çek/senet, personel, kredi kartı ödemesi, kart bakiye işlemi):
 * buradan silinemez, kendi modülünden yönetilir (backend de reddeder).
 */
export const getBankTxLinkedSource = (tx: { referenceType?: string | null; transactionType?: string | null }): BankTxLinkedSource | null => {
  const rt = upper(tx.referenceType);
  const tt = upper(tx.transactionType);
  if (rt.startsWith('CHECK_NOTE') || tt.startsWith('CHECK_NOTE')) return 'check';
  if (rt === 'PERSONNEL_TRANSACTION') return 'personnel';
  if (rt.startsWith('CREDIT_CARD_PAYMENT')) return 'creditCard';
  if (rt === 'CARD_TRANSACTION') return 'card';
  return null;
};
